import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [planName, setPlanName] = useState('');
  const [error, setError] = useState('');
  const { refreshAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/subscriptions/checkout-status/${sessionId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPlanName(data.plan_name || 'your subscription');
          // Refresh auth context to get updated subscription status
          await refreshAuth();
          return;
        }

        if (data.status === 'expired') {
          setStatus('error');
          setError('Payment session expired. Please try again.');
          return;
        }

        // Continue polling if still pending
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('error');
          setError('Payment verification timed out. Please contact support if you were charged.');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('error');
          setError('Failed to verify payment. Please contact support.');
        }
      }
    };

    pollStatus();
  }, [sessionId, refreshAuth]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            IntelliTix<span className="text-emerald-400">®</span>
          </h1>
        </div>

        {/* Status Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <svg className="animate-spin h-12 w-12 text-emerald-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Confirming your payment...
              </h2>
              <p className="text-slate-400">
                Please wait while we verify your subscription.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Welcome to IntelliTix!
              </h2>
              <p className="text-slate-400 mb-2">
                Your subscription to <span className="text-emerald-400 font-medium">{planName}</span> is now active.
              </p>
              <p className="text-slate-500 text-sm mb-8">
                Your 7-day free trial has started. You won't be charged until the trial ends.
              </p>
              <Link
                to="/"
                className="inline-block w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition duration-200"
                data-testid="go-to-app"
              >
                Start Using IntelliTix
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Something went wrong
              </h2>
              <p className="text-slate-400 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="inline-block w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition duration-200"
                >
                  Try Again
                </Link>
                <p className="text-slate-500 text-sm">
                  Need help? Contact support@intellitix.com
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
